import type { CSSProperties, ReactNode, Ref } from "react";
import type { PlotStage, RhythmSky } from "@/shared/contracts";
import { cropArt, referenceCropArt, referencePlantWidth, type CropArt } from "./cropVisual";
import "./garden-bed.css";

/** 开垦土块：UI-ASSET-006 v1（r7k）三张，720×440 透明底、四边留 6% 透明边，没有木框。字节不改。 */
export type BedSoilKey = "a" | "b" | "c";

export interface BedSoil {
  src: string;
  /** 垄沟里的种植点（%，相对土块图 720×440）：作物与地面的交点对准这里。按图里的暗沟逐列量出。 */
  plant: { x: number; y: number };
}

export const BED_SOILS: Record<BedSoilKey, BedSoil> = {
  // 偏圆，四道横垄：种在第二道沟（x 360、y≈180），前面还留两道垄的土
  a: { src: "/ui-assets/UI-ASSET-006/v1/plot-tilled-a.webp", plant: { x: 50, y: 40.9 } },
  // 偏长偏扁，五道横垄：种在正中那道沟（x 360、y≈217）
  b: { src: "/ui-assets/UI-ASSET-006/v1/plot-tilled-b.webp", plant: { x: 50, y: 49.3 } },
  // 不规则，斜垄：种在穿过中部的那道斜沟上（x 400、y≈201）
  c: { src: "/ui-assets/UI-ASSET-006/v1/plot-tilled-c.webp", plant: { x: 55.6, y: 45.7 } },
};

/** 一块地在底图上的位置（都相对底图）：x = 土块图中心横向 %，base = 土块图底边纵向 %，w = 土块图宽度 %；soil = 用哪张土块。 */
export interface BedSlot {
  x: number;
  base: number;
  w: number;
  soil: BedSoilKey;
}

export interface GardenLayout {
  id: string;
  src: string;
  /** 底图宽 / 高。画布按这个比例铺满宽度，坑位百分比一直对得上底图。 */
  aspect: number;
  slots: BedSlot[];
  /** 门边状态泡泡的锚点（%，泡泡贴在锚点右侧、上下居中）。 */
  door: { x: number; y: number };
}

/**
 * UI-ASSET-003 v1 菜园场地（r7k，1080×1660，交付单 docs/coordination/ui-assets/deliveries/UI-ASSET-003-v1.md）。
 * 门约在 (260, 360)；平地 x 8–92%、y 40–86%；底部 86–100% 是可裁的安静草地。
 * 三块地两块在上、一块在下，三张土块轮流用（a 左上、b 右上、c 下）。土块图 720×440 比旧木框扁，
 * 按新比例重排：土块可见部分约占图宽 87%，上排宽 44%（390 宽时土约 150px）、下排近一点放大到 47%；
 * 按钮（整张土块图）面积不小于旧木框时。上排土面中心约在底图 54%、下排约 77%，中间留出木牌和草地
 * （下排的高个作物仍会长到上排木牌那一行，木牌压在作物上面照常可读）。
 */
export const GARDEN_FIELD: GardenLayout = {
  id: "field-v1",
  src: "/ui-assets/UI-ASSET-003/v1/garden-field.webp",
  aspect: 1080 / 1660,
  slots: [
    { x: 28, base: 63, w: 44, soil: "a" },
    { x: 72, base: 63, w: 44, soil: "b" },
    { x: 50, base: 86, w: 47, soil: "c" },
  ],
  door: { x: 33, y: 21 },
};

/** Only explicit garden/neighbor visual049 callers opt in; the courtyard default stays frozen. */
export const GARDEN_VISUAL049: GardenLayout = {
  ...GARDEN_FIELD, id: "visual049", src: "/ui-assets/UI-GARDEN-WEBP-V2-20261013/garden-background.webp", // UI2-GARDEN-8-02：同一张图的有损 webp（2.7MB → 261KB）
  aspect: 1024 / 1536, door: { x: 7, y: 21 },
};

/**
 * UI060-R1：自家菜园按用户第三张参考重做（owner 明确 opt-in；邻居、庭院、visual049 不变）。
 * 一张 1024×1536 逻辑画布：背景 garden-background-v2（屋门、种子棚、小路、侧围栏，地上不画死地块）、土床、作物、前景围栏
 * 一起按“铺满舞台”缩放、居中平移（farm.css 的 .ps-garden-reference-r1）。用户 10-08 要 2.5D（参考经典 QQ 农场）：四个坑位是等距菱形土块
 * （UI049 soil-tilled，项目已有素材），拼成一块带田埂厚度的大菱形田——0 后、1 右、2 左、3 前；坐标写在 farm.css（按 data-slot），
 * 这里的 slots 只作同一套数的记录（x、base 是土块中心，w 是土块宽）；只画 DTO 里真有的地，第 4 格只在权威 expansion 给出时显示待开垦。
 */
export const GARDEN_REFERENCE060R1: GardenLayout = {
  id: "reference060r1",
  src: "/ui-assets/UI-GARDEN-WEBP-V2-20261013/garden-background-v2.webp", // UI2-GARDEN-8-02：同一张图的有损 webp（3.1MB → 409KB）
  aspect: 1024 / 1536,
  slots: [
    { x: 50, base: 51.12, w: 33.5, soil: "a" },
    { x: 66.45, base: 58, w: 33.5, soil: "a" },
    { x: 33.55, base: 58, w: 33.5, soil: "a" },
    { x: 50, base: 64.88, w: 33.5, soil: "a" },
  ],
  door: { x: 17, y: 13 },
};

/**
 * 有夜景底图的白天底图 → 它的夜景（UI2-NIGHT-GARDEN-116，I 10-15 定 A）。按白天底图查、不跟着 layout 走：
 * 邻居菜园用 garden-background-v2，夜里换 r7k 的 ASSET-GARDEN-NIGHT-01（和 v2 逐像素对齐，作者候选 q85 原字节）。
 * 自家菜园是 { ...GARDEN_REFERENCE060R1, src: garden-isometric }，夜里换 r7k 的 ASSET-GARDEN-ISO-NIGHT-01（UI2-GARDEN-2x4-NIGHT-117，I 10-16 收：
 * 和白天几何 1:1，结构处偏移中位 0.38px；作者候选 q85 原字节）。两张都有了，原来那层深藏青加灯光的兜底就撤了。
 */
export const GARDEN_NIGHT_ART: Readonly<Record<string, string>> = {
  [GARDEN_REFERENCE060R1.src]: "/ui-assets/UI-GARDEN-NIGHT-20261015/v1/garden-background-v2-night.webp",
  "/ui-assets/UI-GARDEN-COMPACT-20261012/v1/garden-isometric.webp": "/ui-assets/UI-GARDEN-NIGHT-20261016/v1/garden-isometric-night.webp",
};

/**
 * 自家菜园待开垦格的地块图（r7k ASSET-UI2-GARDEN-PLOTS-02，UI2-GARDEN-2x4-NIGHT-117）：768×768 透明底，菱形落点和已开垦的土块同一个框。
 * 排在下一块的画未开垦（短草、碎石、一根小木桩），后面几块画锁着（草地外一圈矮木栅）；不画锁、链、价签。已开垦照旧是原来的土块。
 */
export const GARDEN_PLOT_STATE_ART = {
  untilled: "/ui-assets/UI-GARDEN-PLOTS-20261016/v1/plot-untilled.webp",
  locked: "/ui-assets/UI-GARDEN-PLOTS-20261016/v1/plot-locked.webp",
} as const;

/** 前景围栏（透明独立层，只装饰、不挡点按）。 */
export const GARDEN_REFERENCE060R1_FENCE = "/ui-assets/UI-GARDEN-WEBP-V2B-20261013/foreground-fence-v1.webp"; // UI2-GARDEN-8-02：同一张图的有损 webp（1.36MB → 323KB）

/** 地块外层的位置；土块图与种植点作为 CSS 变量传给里面的 GardenBed（garden-bed.css 读）。 */
export function slotStyle(slot: BedSlot): CSSProperties {
  const soil = BED_SOILS[slot.soil];
  return {
    left: `${slot.x - slot.w / 2}%`,
    bottom: `${100 - slot.base}%`,
    width: `${slot.w}%`,
    "--bed-soil": `url("${soil.src}")`,
    "--plant-x": String(soil.plant.x),
    "--plant-y": String(soil.plant.y),
  } as CSSProperties;
}

export function anchorStyle(point: { x: number; y: number }): CSSProperties {
  return { left: `${point.x}%`, top: `${point.y}%` };
}

/**
 * 整块场景：画布（底图 + 地 + 泡泡 + 飞行物）按底图比例铺满宽度、顶部对齐；矮屏只裁掉底部安静草地。
 * `hud` 放门牌、篮子这类钉在场景角上的东西（不随裁切丢失）。
 */
export function GardenScene({ layout, label, canvasRef, hud, children, variant, fence = true, sky }: { layout: GardenLayout; label: string; canvasRef?: Ref<HTMLDivElement>; hud?: ReactNode; children: ReactNode; sky?: RhythmSky; variant?: "tools047" | "visual049" | "reference060r1"; fence?: boolean }) {
  const reference = variant === "reference060r1";
  // 夜里换这张白天底图的夜景（UI2-NIGHT-GARDEN-116 邻居、UI2-GARDEN-2x4-NIGHT-117 自家）
  const nightArt = sky === "night" ? GARDEN_NIGHT_ART[layout.src] : undefined;
  return (
    // sky：自家菜园按和小窝同一套时段（后端作息）切昼夜（UI2-REVIEW110-01），邻居菜园跟访客自家的时段（UI2-NIGHT-GARDEN-116）；
    // 夜里换夜景底图，地块压一档，不压界面（farm.css）
    <section className={`ps-garden-scene${variant ? " ps-garden-stage047" : ""}${variant === "visual049" ? " ps-garden-stage049" : ""}${reference ? " ps-garden-reference-r1 ps-garden-contact-stage" : ""}`} aria-label={label} data-sky={sky} data-night-art={nightArt ? "" : undefined}>
      <div className="ps-garden-scene__stage" data-layout={layout.id} style={{ "--scene-aspect": String(layout.aspect) } as CSSProperties}>
        <div ref={canvasRef} className="ps-garden-scene__canvas">
          <img className="ps-garden-scene__bg" src={nightArt ?? layout.src} alt="" />
          {children}
          {/* UI060-R1 前景围栏：画布里的独立透明层，压在土床前面，不接点按（pointer-events: none），不代表串门状态 */}
          {/* 自家菜园的新底图自带围栏，那层本来就被样式藏掉；不挂就不白下 1.36MB（UI2-GARDEN-8-02，fence={false}） */}
          {reference && fence ? <img className="ps-garden-ref-r1__fence" src={GARDEN_REFERENCE060R1_FENCE} alt="" aria-hidden="true" /> : null}
        </div>
        {hud}
      </div>
    </section>
  );
}

/** 作物几何 → CSS 变量（garden-bed.css 按它们把作物的入土点对准种植点）。 */
function artVars(art: CropArt): CSSProperties {
  return {
    "--crop-w": String(art.width),
    "--crop-ax": String(art.anchorX),
    "--crop-ay": String(art.anchorY),
    "--crop-ring": String(art.ring),
    "--crop-top": String(art.top),
    "--crop-l": String(art.left),
    "--crop-r": String(art.right),
  } as CSSProperties;
}

/**
 * 场景里的一块地：开垦土块 + 种在地里的作物（只表达物种与阶段；数量、时间、能不能摘都来自服务端）。
 * 由下往上：土块 → 接地阴影 → 成熟光晕 → 作物 → 前景土层（同一张土块图只露种植线以下，盖住入土的那一截）
 * → 点按延伸（让露在土块上方的作物也能点）。空地与收获后只有土块。
 */
export function GardenBed({ cropKey, stage, planted = false, variant }: { cropKey: string | null; stage: PlotStage; planted?: boolean; variant?: "tools047" | "visual049" | "reference060r1" }) {
  if (variant === "reference060r1") return <ReferenceBed cropKey={cropKey} stage={stage} planted={planted} />;
  const ripe = stage === "ripe";
  const art = stage === "growing" || ripe ? cropArt(cropKey, ripe ? "ripe" : "growing", variant === "visual049" ? "visual049" : undefined) : null;
  return (
    <span
      className={`ps-garden-bed is-tilled is-${stage}${art ? " has-plant" : ""}${planted ? " is-just-planted" : ""}${variant ? " ps-garden-bed047" : ""}${variant === "visual049" ? " ps-garden-bed049" : ""}`}
      data-stage={stage}
      aria-hidden="true"
      style={art ? artVars(art) : undefined}
    >
      <span className="ps-garden-bed__soil" />
      {art ? (
        <>
          <span className="ps-garden-bed__shade" />
          {ripe ? <span className="ps-garden-bed__glow" /> : null}
          <img className={`ps-garden-bed__crop is-${art.key} is-${ripe ? "ripe" : "growing"}${art.root ? " is-root" : ""}`} src={art.src} alt="" />
          <span className="ps-garden-bed__front" />
          <span className="ps-garden-bed__reach" />
        </>
      ) : null}
    </span>
  );
}

/**
 * UI060-R1 的一块地：等距菱形土块（--soil，garden-bed.css）＋三株一丛的作物。作物的实心主体底边落在土床种植线（garden-bed.css 的 --ref-plant-y），
 * 左右中心对准土床中线，按一个宽度比例缩放（不按物种另拉伸）；身后一团接地阴影；根茎作物（两种萝卜）前面再压一层只露种植线以下的同一张土床，
 * 球根下半截像埋在土里。空地、收获后只有土床。
 * 不加整株半透明、不加雾化滤镜（材料包 README）；不画宠物、数量、状态字（状态由 DOM 木牌与读屏文字给）。
 */
function ReferenceBed({ cropKey, stage, planted }: { cropKey: string | null; stage: PlotStage; planted: boolean }) {
  const ripe = stage === "ripe";
  const art = stage === "growing" || ripe ? referenceCropArt(cropKey, ripe ? "ripe" : "growing") : null;
  const vars = art ? ({ "--ref-crop-aspect": String(art.aspect), "--ref-crop-cx": String(art.cx), "--ref-crop-b": String(art.bottom), "--ref-crop-t": String(art.top), "--ref-crop-w": String(referencePlantWidth(cropKey, ripe ? "ripe" : "growing")) } as CSSProperties) : undefined;
  return (
    <span
      className={`ps-garden-bed is-tilled is-${stage}${art ? " has-plant" : ""}${planted ? " is-just-planted" : ""} ps-garden-bed060r1`}
      data-stage={stage}
      aria-hidden="true"
      style={vars}
    >
      <span className="ps-garden-bed__soil" />
      {art ? (
        <>
          <span className="ps-garden-bed__shade" />
          {ripe ? <span className="ps-garden-bed__glow" /> : null}
          <img className={`ps-garden-bed__crop is-${art.key} is-${ripe ? "ripe" : "growing"}${art.root ? " is-root" : ""}`} src={art.src} alt="" />
          {/* 根茎作物：同一张土床图只露种植线以下，盖住入土的那一截（球根下半截在土里） */}
          <span className={`ps-garden-bed__front${art.root ? " is-root" : " is-stem"}`} />
          <span className="ps-garden-bed__reach" />
        </>
      ) : null}
    </span>
  );
}

/** Actual current-home canonical IDs keep their physical slot across DTO reorder.
 * Legacy/foreign/noncanonical IDs retain bounded DTO-order fallback; this is only
 * presentation, never authority to create a plot or replace its command identity. */
function boundedGardenSlot(slot:number) {
 return Number.isSafeInteger(slot)&&slot>=0?Math.min(slot,7):0;
}
export function gardenSlot(homeId:string,plotId:string,index:number) {
 const suffix=homeId&&plotId.startsWith(`${homeId}-p`)?plotId.slice(`${homeId}-p`.length):"";
 return /^[0-7]$/.test(suffix)?Number(suffix):boundedGardenSlot(index);
}
export function gardenGridStyle(slot:number):CSSProperties {
 const safe=boundedGardenSlot(slot),local=safe%4;
 const column=local%2,row=Math.floor(local/2);
 // Preserve the original four-cell diamond, then use positive tracks below it.
 return {gridColumn:`${2+column-row} / span 2`,gridRow:1+column+row+3*Math.floor(safe/4)};
}
